import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components/macro';
import { toast } from 'react-toastify';
import notify from 'Common/utils/notify';
import axios from 'axios';
import { ExitToAppSharp } from '@material-ui/icons';
import FormField from 'Common/components/form/FormField';
import Input from 'Common/components/form/fields/Input';
import Button from 'Common/components/Button';
import login from 'User/services/login';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import useAxiosRequest from 'Common/hooks/useAxiosRequest';
import getPlatformDomainName from 'Application/api/getPlatformDomainName';
import Loader from 'Common/components/Loader';
import useQueryParameter from 'Common/hooks/useQueryParameter';

const validationSchema = Yup.object().shape({
    domain: Yup.string().required('Pflichtangabe'),
    userIdentifier: Yup.string().required('Pflichtangabe'),
    password: Yup.string().required('Pflichtangabe'),
});

const LoginButton = styled(Button)`
    margin-top: 10px;

    @media screen and (min-width: 600px) {
        max-width: 270px;
    }
`;

const LoginForm = () => {
    const { data: platformDomainNameData, isLoading } = useAxiosRequest(
        getPlatformDomainName,
        [],
    );

    const [domainQueryParameter] = useQueryParameter('domain');

    const platformDomainName =
        platformDomainNameData.domain ?? domainQueryParameter;

    const source = useMemo(() => axios.CancelToken.source(), []);

    useEffect(
        () => () => {
            source.cancel();
        },
        [source],
    );

    const initialValues = useMemo(
        () => ({
            domain: platformDomainName ?? '',
            userIdentifier: '',
            password: '',
        }),
        [platformDomainName],
    );

    const {
        errors,
        touched,
        values,
        handleChange,
        handleBlur,
        handleSubmit,
    } = useFormik({
        enableReinitialize: true,
        initialValues,
        validationSchema,
        onSubmit: async values => {
            try {
                await login(
                    values.domain,
                    values.userIdentifier,
                    values.password,
                    true,
                    source.token,
                );
            } catch (error) {
                if (!axios.isCancel(error)) {
                    notify('Zugangsdaten ungültig', {
                        type: toast.TYPE.ERROR,
                    });
                }
            }
        },
    });

    if (isLoading) {
        return <Loader />;
    }

    return (
        <form onSubmit={handleSubmit}>
            <FormField>
                <Input
                    label="Domain"
                    id="domain"
                    name="domain"
                    error={errors.domain}
                    touched={touched.domain}
                    value={values.domain}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    spellCheck={false}
                    readOnly={!!platformDomainName}
                />
            </FormField>
            <FormField>
                <Input
                    label="Benutzername"
                    id="userIdentifier"
                    name="userIdentifier"
                    error={errors.userIdentifier}
                    touched={touched.userIdentifier}
                    value={values.userIdentifier}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    spellCheck={false}
                />
            </FormField>
            <FormField>
                <Input
                    type="password"
                    label="Passwort"
                    id="password"
                    name="password"
                    error={errors.password}
                    touched={touched.password}
                    value={values.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                />
            </FormField>

            <LoginButton type="submit">
                <ExitToAppSharp fontSize="small" />
                Anmelden
            </LoginButton>
        </form>
    );
};

export default LoginForm;
